import Select from 'react-select'
import {MultiSelect} from 'react-multi-select-component'
import {useRoomForm} from '../core/RoomFormProvider'
import {TimeRoomRepeaterForm} from './TimeRoomRepeaterForm'
import {RoomOption} from '../core/_models'
import {ISelect} from '../../../coreGlobal/models/default'
import {ProductSelect} from '../../../components/ProductSelect'

interface IRoomForm {
  formik: any
  isEdit?: boolean
}

const selectedOptions = <T extends RoomOption | ISelect>(options: T[], values: Array<number> = []) =>
  options.filter((option) => values.some((value) => Number(value) === Number(option.value)))

const RoomForm: React.FC<IRoomForm> = ({formik, isEdit = false}) => {
  const {navigate, loading, franchises, categories, products, getDataProducts, machines, getDataMachines} =
    useRoomForm()

  const hasLaserCategory = formik.values.categories?.some((category) => Number(category) === 1)

  return (
    <form onSubmit={formik.handleSubmit} noValidate className='form'>
      <div className='card mb-5 mb-xl-10'>
        <div className='card-header border-0 pt-6'>
          <div className='card-title'>
            <h3 className='fw-bold mb-1'>Dados da sala</h3>
          </div>
        </div>
        <div className='card-body border-top p-9'>
          <div className='row mb-6'>
            <label className='col-lg-4 col-form-label fw-bold fs-6'>
              <span className='required'>Franquia</span>
            </label>
            <div className='col-lg-8 fv-row'>
              <Select
                className='react-select-styled react-select-solid'
                classNamePrefix='react-select'
                placeholder='Selecione a franquia'
                noOptionsMessage={() => 'Nenhuma franquia encontrada'}
                isDisabled={isEdit}
                onChange={(option) => {
                  const franchiseId = option ? Number(option.value) : null
                  formik.setFieldValue('franchise', franchiseId)
                  // As máquinas são da franquia, então a seleção anterior deixa de valer
                  formik.setFieldValue('machines', [])
                  getDataMachines(franchiseId)
                }}
                options={franchises}
                value={
                  franchises.find(
                    (franchise: ISelect) => franchise.value === String(formik.values.franchise)
                  ) ?? null
                }
              />
            </div>
          </div>

          <div className='row mb-6'>
            <label className='col-lg-4 col-form-label fw-bold fs-6'>
              <span className='required'>Nome</span>
            </label>
            <div className='col-lg-8 fv-row'>
              <input
                type='text'
                className='form-control form-control-lg form-control-solid'
                placeholder='Nome da sala'
                {...formik.getFieldProps('name')}
                maxLength={64}
              />
            </div>
          </div>

          <div className='row mb-6'>
            <label className='col-lg-4 col-form-label fw-bold fs-6'>
              <span className='required'>Nome sincronização</span>
            </label>
            <div className='col-lg-8 fv-row'>
              <input
                type='text'
                className='form-control form-control-lg form-control-solid'
                placeholder='Nome da sala para sincronização'
                {...formik.getFieldProps('syncName')}
                maxLength={64}
              />
            </div>
          </div>

          <div className='row mb-6'>
            <label className='col-lg-4 col-form-label fw-bold fs-6'>
              <span className='required'>Categoria</span>
            </label>
            <div className='col-lg-8 fv-row'>
              <Select
                isMulti
                className='react-select-styled react-select-solid'
                classNamePrefix='react-select'
                placeholder='Selecione as categorias'
                onChange={(option) => {
                  const values = option.map((item) => Number(item.value))
                  formik.setFieldValue('categories', values)
                  getDataProducts(values)
                }}
                options={categories}
                value={selectedOptions(categories, formik.values.categories)}
              />
            </div>
          </div>

          <div className='row mb-6'>
            <label className='col-lg-4 col-form-label fw-bold fs-6'>
              <span className='required'>Produtos</span>
            </label>
            <div className='col-lg-8 fv-row'>
              <ProductSelect
                isMulti
                closeMenuOnSelect={false}
                hideSelectedOptions
                options={products}
                value={selectedOptions(products, formik.values.products)}
                onChange={(options) => {
                  formik.setFieldValue(
                    'products',
                    options.map((item) => Number(item.value))
                  )
                }}
                placeholder='Selecione os produtos'
              />
              <div className='d-flex gap-4 mt-2'>
                {/* Inativos ficam de fora do "todos", mas os já selecionados são mantidos */}
                <button
                  type='button'
                  className='btn btn-link btn-color-danger btn-active-color-danger p-0 fs-7'
                  disabled={!products.length}
                  onClick={() => {
                    const selected = selectedOptions(products, formik.values.products)
                    formik.setFieldValue(
                      'products',
                      products
                        .filter((product) => product.active || selected.includes(product))
                        .map((product) => Number(product.value))
                    )
                  }}
                >
                  Selecionar todos
                </button>
                <button
                  type='button'
                  className='btn btn-link btn-color-muted btn-active-color-danger p-0 fs-7'
                  disabled={!formik.values.products?.length}
                  onClick={() => formik.setFieldValue('products', [])}
                >
                  Limpar
                </button>
              </div>
            </div>
          </div>

          <div className='row mb-6' hidden={!hasLaserCategory}>
            <label className='col-lg-4 col-form-label fw-bold fs-6'>
              <span className='required'>Máquinas</span>
            </label>
            <div className='col-lg-8 fv-row'>
              <MultiSelect
                className='react-select-styled react-select-solid'
                options={machines}
                value={selectedOptions(machines, formik.values.machines)}
                disabled={!formik.values.franchise}
                onChange={(option) => {
                  formik.setFieldValue(
                    'machines',
                    option.map((item) => Number(item.value))
                  )
                }}
                labelledBy={''}
              />
            </div>
          </div>

          <div className='row mb-6'>
            <label className='col-lg-4 col-form-label fw-bold fs-6'>
              <span className='required'>Range de agendamento</span>
            </label>
            <div className='col-lg-8 fv-row'>
              <input
                type='number'
                className='form-control form-control-lg form-control-solid'
                placeholder='Tempo máximo de agendamento em dia'
                {...formik.getFieldProps('maxScheduleDate')}
              />
            </div>
          </div>

          <div className='row mb-6'>
            <label className='col-lg-4 col-form-label fw-bold fs-6'>
              <span className='required'>Cor</span>
            </label>
            <div className='col-lg-8 fv-row'>
              <input
                type='color'
                className='form-control form-control-lg form-control-solid'
                {...formik.getFieldProps('color')}
              />
            </div>
          </div>

          <div className='row'>
            <label className='col-lg-4 col-form-label fw-bold fs-6'>
              <span className='required'>Disponibilidade da sala</span>
            </label>
          </div>
          <div className='row'>
            <div className='col-lg-12 fv-row mb-6'>
              <div className='py-5 table-responsive'>
                <table className='table table-row-dashed table-bordered table-row-gray-300 gy-7'>
                  <thead>
                    <tr className='fw-bolder fs-6 text-gray-800'>
                      <th className='min-w-200px'>Domingo</th>
                      <th className='min-w-200px'>Segunda</th>
                      <th className='min-w-200px'>Terça</th>
                      <th className='min-w-200px'>Quarta</th>
                      <th className='min-w-200px'>Quinta</th>
                      <th className='min-w-200px'>Sexta</th>
                      <th className='min-w-200px'>Sábado</th>
                    </tr>
                  </thead>
                  <tbody>
                    <TimeRoomRepeaterForm formik={formik} />
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className='card-footer d-flex justify-content-end py-6 px-9'>
          <button type='button' className='btn btn-secondary me-2' onClick={() => navigate(-1)}>
            Cancelar
          </button>

          <button type='submit' className='btn btn-danger' disabled={loading || !formik.isValid}>
            {!loading && 'Salvar'}
            {loading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                Salvando...{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </div>
    </form>
  )
}

export {RoomForm}
